import React, { useRef, useState } from "react";
import html2canvas from "html2canvas";
import { toast } from "sonner";
import ShareCard from "../components/ShareCard";
import BotanicalMark from "../components/BotanicalMark";
import { useLang } from "../i18n/LanguageContext";

const CONSULT_URL = process.env.REACT_APP_CONSULTATION_URL || "#";
const WHATSAPP_URL = process.env.REACT_APP_WHATSAPP_URL || "#";
const API = process.env.REACT_APP_BACKEND_URL;

export default function Result({ result, onRestart }) {
  const { t, lang } = useLang();
  const cardRef = useRef(null);
  const [busy, setBusy] = useState(false);
  const [email, setEmail] = useState("");
  const [consent, setConsent] = useState(false);
  const [subscribing, setSubscribing] = useState(false);
  const [subscribed, setSubscribed] = useState(false);
  const [o1, o2] = result.oils;

  const exportCanvas = async () => {
    if (!cardRef.current) return null;
    const canvas = await html2canvas(cardRef.current, {
      backgroundColor: "#FAF8F5",
      scale: 1,
      useCORS: true,
      logging: false,
    });
    return canvas;
  };

  const handleSave = async () => {
    try {
      setBusy(true);
      const canvas = await exportCanvas();
      const link = document.createElement("a");
      link.download = `aroma-compass-${result.name
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")}.png`;
      link.href = canvas.toDataURL("image/png");
      link.click();
      toast.success(t.toastSaveOk);
    } catch (e) {
      toast.error(t.toastSaveErr);
    } finally {
      setBusy(false);
    }
  };

  const handleShare = async () => {
    try {
      setBusy(true);
      const canvas = await exportCanvas();
      const blob = await new Promise((res) => canvas.toBlob(res, "image/png"));
      const file = new File([blob], "aroma-compass.png", { type: "image/png" });

      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({
          title: "Aroma Compass",
          text: `${result.name} · ${o1.name} + ${o2.name}`,
          files: [file],
        });
        toast.success(t.toastShareOk);
      } else {
        await handleSave();
      }
    } catch (e) {
      if (e?.name !== "AbortError") {
        toast.error(t.toastShareErr);
      }
    } finally {
      setBusy(false);
    }
  };

  const handleSubscribe = async (e) => {
    e.preventDefault();
    if (!consent) {
      toast.error(t.nlErrConsent);
      return;
    }
    const cleaned = email.trim();
    if (!cleaned || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(cleaned)) {
      toast.error(t.nlErrEmail);
      return;
    }
    try {
      setSubscribing(true);
      const res = await fetch(`${API}/api/newsletter/subscribe`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email: cleaned,
          consent: true,
          source: "aroma_compass_result",
        }),
      });
      if (!res.ok) throw new Error("subscribe_failed");
      const data = await res.json();
      setSubscribed(true);
      if (data.status === "already_subscribed") {
        toast.success(t.nlOkAlready);
      } else {
        toast.success(t.nlOkSubscribed);
      }
    } catch (err) {
      toast.error(t.nlErrGeneric);
    } finally {
      setSubscribing(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full" data-testid="result-page">
      <div className="ac-ambient" />
      <BotanicalMark className="absolute -right-6 top-24 hidden sm:block" size={130} opacity={0.22} />

      <div className="relative z-10 max-w-xl mx-auto px-5 sm:px-8 pt-10 pb-16">
        <header className="flex items-center justify-between mb-10">
          <span className="ac-eyebrow" data-testid="result-brand">{t.resultBrand}</span>
          <button
            onClick={onRestart}
            className="text-[11px] font-mono uppercase tracking-[0.24em] text-[color:var(--ac-ink-soft)] hover:text-[color:var(--ac-ink)] transition"
            data-testid="result-restart-top"
          >
            {t.resultRestartShort}
          </button>
        </header>

        <p className="font-serif italic text-lg text-[color:var(--ac-ink-soft)] ac-in mb-4" data-testid="result-lead">
          {t.resultLead}
        </p>

        <h1
          className="ac-display text-[40px] sm:text-6xl mb-6 ac-in ac-in-delay-1"
          data-testid="result-synergy-name"
        >
          {result.name}
        </h1>

        <p className="font-serif text-2xl sm:text-3xl mb-3 ac-in ac-in-delay-2" data-testid="result-oils">
          {o1.name} <span className="text-[color:var(--ac-gold)]">+</span> {o2.name}
        </p>

        <p className="font-mono text-xs sm:text-sm uppercase tracking-[0.24em] text-[color:var(--ac-malva)] ac-in ac-in-delay-2" data-testid="result-keywords">
          {result.keywords[0]} <span className="mx-2 text-[color:var(--ac-gold)]">×</span> {result.keywords[1]}
        </p>

        <hr className="ac-hairline my-10" />

        <p className="ac-eyebrow mb-3 ac-in ac-in-delay-3">{t.resultInterpretationLabel}</p>
        <p
          className="font-sans text-[15px] sm:text-lg leading-relaxed text-[color:var(--ac-ink)] ac-in ac-in-delay-3"
          data-testid="result-interpretation"
        >
          {result.interpretation}
        </p>

        <div
          className="mt-10 rounded-3xl p-6 sm:p-8 ac-in ac-in-delay-4"
          style={{
            background: "linear-gradient(180deg, #F7F3E9 0%, #F3EFEA 100%)",
            border: "1px solid var(--ac-border)",
          }}
        >
          <p className="ac-eyebrow mb-3">{t.resultRitualLabel}</p>
          <p className="font-serif text-xl sm:text-2xl leading-snug mb-6" data-testid="result-ritual">
            {result.ritual}
          </p>
          <hr className="ac-hairline mb-6" />
          <p className="ac-eyebrow mb-3">{t.resultIntentionLabel}</p>
          <p
            className="font-serif italic text-2xl sm:text-3xl leading-tight text-[color:var(--ac-ink)]"
            data-testid="result-intention"
          >
            «{result.intention}»
          </p>
        </div>

        <div className="mt-10 flex flex-col gap-3">
          <button
            className="ac-btn-primary w-full"
            onClick={handleSave}
            disabled={busy}
            data-testid="save-result-button"
          >
            {busy ? t.resultSaving : t.resultSave}
          </button>
          <div className="grid grid-cols-2 gap-3">
            <button
              className="ac-btn-ghost w-full"
              onClick={handleShare}
              disabled={busy}
              data-testid="share-result-button"
            >
              {t.resultShare}
            </button>
            <button
              className="ac-btn-ghost w-full"
              onClick={onRestart}
              data-testid="restart-quiz-button"
            >
              {t.resultRestart}
            </button>
          </div>
        </div>

        <div
          className="mt-16 rounded-3xl p-6 sm:p-8 ac-in ac-in-delay-5 text-center"
          style={{
            background: "#FFFFFF",
            border: "1px solid var(--ac-border)",
          }}
        >
          <p className="font-serif italic text-2xl sm:text-3xl mb-3">
            {t.resultCtaTitle}
          </p>
          <p className="font-sans text-[14px] sm:text-base leading-relaxed text-[color:var(--ac-ink-soft)] mb-6">
            {t.resultCtaBody}
          </p>
          <a
            className="ac-btn-primary inline-flex"
            href={CONSULT_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="cta-consultations"
          >
            {t.resultCtaButton}
          </a>

          <p className="mt-6 font-sans text-[13px] sm:text-sm leading-relaxed text-[color:var(--ac-ink-muted)]">
            {t.resultWhatsapp}{" "}
            <a
              className="font-serif italic text-[color:var(--ac-ink-soft)] underline decoration-[color:var(--ac-gold)] decoration-1 underline-offset-4 hover:text-[color:var(--ac-ink)] transition"
              href={WHATSAPP_URL}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="cta-whatsapp"
            >
              {t.resultWhatsappLink}
            </a>
          </p>
        </div>

        <div
          className="mt-8 rounded-3xl p-6 sm:p-8 ac-in ac-in-delay-5"
          style={{
            background: "linear-gradient(180deg, #F7F3E9 0%, #FAF8F5 100%)",
            border: "1px solid var(--ac-border)",
          }}
          data-testid="newsletter-panel"
        >
          {subscribed ? (
            <div className="text-center py-2" data-testid="newsletter-thanks">
              <p className="font-serif italic text-2xl mb-2">
                {t.nlThanksTitle}
              </p>
              <p className="font-sans text-[14px] leading-relaxed text-[color:var(--ac-ink-soft)]">
                {t.nlThanksBody}
              </p>
            </div>
          ) : (
            <form onSubmit={handleSubscribe} className="flex flex-col gap-4">
              <div>
                <p className="ac-eyebrow mb-2">{t.nlEyebrow}</p>
                <p className="font-serif text-xl sm:text-2xl leading-tight mb-2">
                  {t.nlTitle}
                </p>
                <p className="font-sans text-[13px] sm:text-sm leading-relaxed text-[color:var(--ac-ink-soft)]">
                  {t.nlBody}
                </p>
              </div>

              <input
                type="email"
                autoComplete="email"
                inputMode="email"
                placeholder={t.nlEmailPlaceholder}
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                data-testid="newsletter-email"
                className="w-full rounded-full px-5 py-4 bg-white border font-sans text-[15px] text-[color:var(--ac-ink)] placeholder:text-[color:var(--ac-ink-muted)] focus:outline-none focus:border-[color:var(--ac-gold)] transition"
                style={{ borderColor: "var(--ac-border-strong)" }}
              />

              <label className="flex items-start gap-3 cursor-pointer" data-testid="newsletter-consent-label">
                <input
                  type="checkbox"
                  checked={consent}
                  onChange={(e) => setConsent(e.target.checked)}
                  data-testid="newsletter-consent"
                  className="mt-1 accent-[color:var(--ac-gold)] w-4 h-4 shrink-0"
                />
                <span className="font-sans text-[12px] leading-relaxed text-[color:var(--ac-ink-soft)]">
                  {t.nlConsent}
                </span>
              </label>

              <button
                type="submit"
                disabled={subscribing || !consent}
                className="ac-btn-primary w-full disabled:opacity-40 disabled:cursor-not-allowed"
                data-testid="newsletter-submit"
              >
                {subscribing ? t.nlSubmitting : t.nlSubmit}
              </button>
            </form>
          )}
        </div>

        <p className="mt-14 text-center font-serif italic text-[13px] text-[color:var(--ac-ink-muted)]">
          {t.resultAuthor}
        </p>
      </div>

      <div
        aria-hidden="true"
        style={{
          position: "fixed",
          left: "-99999px",
          top: 0,
          pointerEvents: "none",
        }}
      >
        <ShareCard ref={cardRef} result={result} lang={lang} />
      </div>
    </div>
  );
}
